import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Archive,
  Check,
  Copy,
  Download,
  Edit,
  Lock,
  QrCode,
  Share2,
  Trash2,
  Vote,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "@core/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@core/components/ui/tooltip";
import type { Poll } from "@core/lib/pollStorage";
import type { UnifiedPoll } from "@core/lib/pollStorageUnified";
import {
  buildPublicLink,
  copyToClipboard,
  duplicatePoll,
  getAllPolls,
  addPoll,
} from "@core/lib/pollStorage";
import {
  exportFormPollToCSV,
  exportFormPollToPDF,
  exportFormPollToJSON,
  exportFormPollToMarkdown,
  hasExportableData,
} from "@core/lib/exports";
import { ErrorFactory, logError } from "@core/lib/error-handling";
import {
  compareSimulationWithReality,
  getLastSimulation,
} from "@core/lib/simulation/SimulationComparison";
import { usePollDeletionCascade } from "@core/hooks/usePollDeletionCascade";
import {
  anonymizeVotesForPoll,
  deleteVotesByPollId,
} from "@core/lib/pollStorage";
import { createConversationForPoll } from "@core/lib/ConversationPollLink";
import { QRCodeDisplay } from "@core/components/shared/QRCodeDisplay";
import { pollService } from "@core/services/PollService";

export type PollActionsVariant = "compact" | "full";

interface PollActionsProps {
  poll: Poll;
  showVoteButton?: boolean;
  variant?: PollActionsVariant;
  className?: string;
  onEdit?: (pollId: string) => void;
  onAfterDuplicate?: (newPoll: Poll) => void;
  onAfterDelete?: () => void;
  onAfterArchive?: () => void;
  onAfterClose?: () => void;
}

export const PollActions: React.FC<PollActionsProps> = ({
  poll,
  showVoteButton = true,
  variant = "full",
  className,
  onEdit,
  onAfterDuplicate,
  onAfterDelete,
  onAfterArchive,
  onAfterClose,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isCopied, setIsCopied] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const { deletePollWithCascade } = usePollDeletionCascade();
  const preloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation("polls");

  const handleCopyLink = async () => {
    try {
      const url = buildPublicLink(poll.slug);
      await copyToClipboard(url);
      setIsCopied(true);
      toast({
        title: t("actions.copyLink.successTitle"),
        description: t("actions.copyLink.successDescription"),
      });
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast({
        title: t("actions.errorTitle"),
        description: t("actions.copyLink.errorDescription"),
        variant: "destructive",
      });
    }
  };

  const handleAnonymizeVotes = () => {
    if (
      poll.type !== "date" ||
      !window.confirm(t("actions.anonymize.confirm"))
    ) {
      return;
    }

    try {
      const { anonymizedCount } = anonymizeVotesForPoll(poll.id);

      if (anonymizedCount > 0) {
        toast({
          title: t("actions.anonymize.successTitle"),
          description: t("actions.anonymize.successDescription", {
            count: anonymizedCount,
          }),
        });
      } else {
        toast({
          title: t("actions.anonymize.emptyTitle"),
          description: t("actions.anonymize.emptyDescription"),
        });
      }
    } catch (error) {
      logError(error, {
        component: "PollActions",
        operation: "anonymizeVotes",
      });
      toast({
        title: t("actions.errorTitle"),
        description:
          error instanceof Error
            ? error.message
            : t("actions.anonymize.errorDescription"),
        variant: "destructive",
      });
    }
  };

  const handleEdit = () => {
    if (onEdit) return onEdit(poll.id);
    if (poll.type === "form") {
      navigate(`/workspace/form?edit=${poll.id}`);
    } else {
      navigate(`/workspace/date?edit=${poll.id}`);
    }
  };

  const handlePreloadEdit = () => {
    // Précharger PollCreator si c'est un sondage de dates (pas formulaire)
    if (poll.type !== "form") {
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
      preloadTimeoutRef.current = setTimeout(() => {
        const preloadFn = (
          window as Window & { preloadPollCreator?: () => void }
        ).preloadPollCreator;
        if (typeof preloadFn === "function") {
          preloadFn();
        }
      }, 300);
    }
  };

  const handleMouseLeaveEdit = () => {
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
      preloadTimeoutRef.current = null;
    }
  };

  const handleDuplicate = async () => {
    try {
      const dup = await duplicatePoll(poll);

      // Le poll est déjà créé dans Supabase via duplicatePoll()
      // Create a conversation for the duplicated poll
      const pollType =
        dup.type === "availability" ? "date" : dup.type || "date";
      createConversationForPoll(dup.id, dup.title, pollType);

      toast({
        title: t("actions.duplicate.successTitle"),
        description: t("actions.duplicate.successDescription"),
      });
      onAfterDuplicate?.(dup);
    } catch (error) {
      logError(error, { component: "PollActions", operation: "duplicatePoll" });
      toast({
        title: t("actions.errorTitle"),
        description: t("actions.duplicate.errorDescription"),
        variant: "destructive",
      });
    }
  };

  const handleArchive = async () => {
    try {
      await pollService.updatePoll(poll.id, {
        status: "archived",
        updated_at: new Date().toISOString(),
      });
      // Mettre à jour le cache local pour refléter immédiatement le changement
      addPoll({
        ...poll,
        status: "archived",
        updated_at: new Date().toISOString(),
      });
      toast({
        title: t("actions.archive.successTitle"),
        description: t("actions.archive.successDescription"),
      });
      onAfterArchive?.();
    } catch {
      toast({
        title: t("actions.errorTitle"),
        description: t("actions.archive.errorDescription"),
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    if (!window.confirm(t("actions.close.confirm"))) return;
    try {
      const updatedPoll = {
        ...poll,
        status: "closed" as const,
        updated_at: new Date().toISOString(),
      };
      addPoll(updatedPoll);

      // Déclencher comparaison simulation si applicable
      if (poll.type === "form") {
        const lastSimulation = getLastSimulation(poll.id);
        if (lastSimulation) {
          try {
            const comparison = compareSimulationWithReality(
              poll.id,
              lastSimulation,
            );
            toast({
              title: t("actions.close.formSuccessTitle"),
              description: t("actions.close.simulationAccuracy", {
                accuracy: comparison.accuracy.overall,
              }),
            });
          } catch (error) {
            toast({
              title: t("actions.close.formSuccessTitle"),
              description: t("actions.close.formSuccessDescription"),
            });
          }
        } else {
          toast({
            title: t("actions.close.formSuccessTitle"),
            description: t("actions.close.formSuccessDescription"),
          });
        }
      } else {
        toast({
          title: t("actions.close.pollSuccessTitle"),
          description: t("actions.close.pollSuccessDescription"),
        });
      }
      onAfterClose?.();
    } catch {
      toast({
        title: t("actions.errorTitle"),
        description: t("actions.close.errorDescription"),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    const confirmMessage = t("actions.delete.confirm");

    if (!window.confirm(confirmMessage)) return;

    try {
      // Delete votes first
      deleteVotesByPollId(poll.id);

      // Delete poll and conversations
      const result = await deletePollWithCascade(poll.id, {
        deleteConversation: true,
      });

      if (result.success) {
        let description = t("actions.delete.successDescription");
        if (result.conversationDeleted) {
          description += " " + t("actions.delete.conversationsDeleted");
        }

        toast({
          title: t("actions.delete.successTitle"),
          description,
        });
        onAfterDelete?.();
      } else {
        throw ErrorFactory.storage(
          "Delete failed",
          "Erreur lors de la suppression du sondage",
        );
      }
    } catch (error) {
      logError(error, { component: "PollActions", operation: "handleDelete" });
      toast({
        title: t("actions.delete.errorTitle"),
        description: t("actions.delete.errorDescription"),
        variant: "destructive",
      });
    }
  };

  // Convert Poll to UnifiedPoll for exports
  const unifiedPoll: UnifiedPoll = {
    id: poll.id,
    type: poll.type,
    title: poll.title,
    description: poll.description,
    slug: poll.slug,
    poll_data_json: poll as any, // Poll legacy contient les données
    creator_id: poll.creator_id,
    creator_type: poll.creator_type,
    guest_fingerprint: poll.guest_fingerprint,
    guest_session_id: poll.guest_session_id,
    status: poll.status,
    created_at: poll.created_at,
    updated_at: poll.updated_at,
  };

  const handleExport = (format: "csv" | "pdf" | "json" | "markdown") => {
    try {
      if (poll.type !== "form") {
        toast({
          title: t("actions.export.unsupportedTitle"),
          description: t("actions.export.unsupportedDescription"),
          variant: "destructive",
        });
        return;
      }

      switch (format) {
        case "csv":
          exportFormPollToCSV(unifiedPoll);
          toast({
            title: t("actions.export.csvSuccessTitle"),
            description: t("actions.export.csvSuccessDescription"),
          });
          break;
        case "pdf":
          exportFormPollToPDF(unifiedPoll);
          toast({
            title: t("actions.export.pdfSuccessTitle"),
            description: t("actions.export.pdfSuccessDescription"),
          });
          break;
        case "json":
          exportFormPollToJSON(unifiedPoll);
          toast({
            title: t("actions.export.jsonSuccessTitle"),
            description: t("actions.export.jsonSuccessDescription"),
          });
          break;
        case "markdown":
          exportFormPollToMarkdown(unifiedPoll);
          toast({
            title: t("actions.export.markdownSuccessTitle"),
            description: t("actions.export.markdownSuccessDescription"),
          });
          break;
      }
    } catch (error) {
      logError(
        error instanceof Error
          ? error
          : ErrorFactory.api(
              "Export error",
              "Erreur lors de l'export du sondage",
            ),
        {
          component: "PollActions",
          operation: "handleExport",
          pollId: poll.id,
        },
      );
      toast({
        title: t("actions.export.errorTitle"),
        description:
          error instanceof Error
            ? error.message
            : t("actions.export.errorDescription"),
        variant: "destructive",
      });
    }
  };

  return (
    <TooltipProvider>
      <div>
        <div className={`flex flex-wrap items-center gap-2 ${className ?? ""}`}>
          {showVoteButton && poll.status !== "draft" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate(`/poll/${poll.slug}`)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  data-testid="results-action-vote"
                >
                  <Vote className="w-4 h-4" />
                  {t("actions.voteButton")}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {poll.type === "form"
                    ? t("actions.vote.tooltipForm")
                    : t("actions.vote.tooltipPoll")}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block">
                <button
                  onClick={handleCopyLink}
                  className={`p-2 rounded-md transition-all duration-300 flex items-center gap-1 ${
                    isCopied
                      ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                      : "bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                  }`}
                  data-testid="poll-action-copy-link"
                  disabled={isCopied}
                >
                  {isCopied ? (
                    <>
                      <Check className="w-4 h-4 animate-in zoom-in duration-200" />
                      {variant === "full" && (
                        <span className="hidden sm:inline font-medium">
                          {t("actions.copyLink.copied")}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      {variant === "full" && (
                        <span className="hidden sm:inline">
                          {t("actions.copyLink.label")}
                        </span>
                      )}
                    </>
                  )}
                </button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {isCopied
                  ? t("actions.copyLink.tooltipCopied")
                  : t("actions.copyLink.tooltip")}
              </p>
            </TooltipContent>
          </Tooltip>

          {poll.status !== "draft" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setShowQR(!showQR)}
                  className={`p-2 rounded-md transition-all duration-300 flex items-center gap-1 ${
                    showQR
                      ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                      : "bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                  }`}
                  data-testid="poll-action-qr"
                >
                  <QrCode className="w-4 h-4" />
                  {variant === "full" && (
                    <span className="hidden sm:inline">
                      {t("actions.qr.label")}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {showQR
                    ? t("actions.qr.tooltipHide")
                    : t("actions.qr.tooltipShow")}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          {poll.type === "form" && hasExportableData(unifiedPoll) && (
            <div className="relative">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-3 py-2 rounded-md text-sm font-medium hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors flex items-center gap-1"
                    data-testid="poll-action-export"
                  >
                    <Download className="w-4 h-4" />
                    {variant === "full" && (
                      <span>{t("actions.export.label")}</span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("actions.export.tooltip")}</p>
                </TooltipContent>
              </Tooltip>

              {showExportMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowExportMenu(false)}
                  />
                  <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                    <button
                      onClick={() => {
                        handleExport("csv");
                        setShowExportMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded-t-md"
                      data-testid="export-csv"
                    >
                      📊 CSV
                    </button>
                    <button
                      onClick={() => {
                        handleExport("pdf");
                        setShowExportMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      data-testid="export-pdf"
                    >
                      📄 PDF
                    </button>
                    <button
                      onClick={() => {
                        handleExport("json");
                        setShowExportMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      data-testid="export-json"
                    >
                      🔧 JSON
                    </button>
                    <button
                      onClick={() => {
                        handleExport("markdown");
                        setShowExportMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded-b-md"
                      data-testid="export-markdown"
                    >
                      📝 Markdown
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleEdit}
                onMouseEnter={handlePreloadEdit}
                onMouseLeave={handleMouseLeaveEdit}
                className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                data-testid="poll-action-edit"
              >
                <Edit className="w-4 h-4" />
                {variant === "full" && <span>{t("actions.edit.label")}</span>}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("actions.edit.tooltip")}</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleDuplicate}
                className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                data-testid="poll-action-duplicate"
              >
                <Copy className="w-4 h-4" />
                {variant === "full" && (
                  <span>{t("actions.duplicate.label")}</span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("actions.duplicate.tooltip")}</p>
            </TooltipContent>
          </Tooltip>

          {poll.status === "active" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleClose}
                  className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                  data-testid="poll-action-close"
                >
                  <Lock className="w-4 h-4" />
                  {variant === "full" && (
                    <span className="hidden sm:inline">
                      {t("actions.close.label")}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("actions.close.tooltip")}</p>
              </TooltipContent>
            </Tooltip>
          )}

          {poll.type === "date" && poll.status !== "archived" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleAnonymizeVotes}
                  className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                  data-testid="poll-action-anonymize-votes"
                >
                  <Lock className="w-4 h-4" />
                  {variant === "full" && (
                    <span className="hidden sm:inline">
                      {t("actions.anonymize.label")}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("actions.anonymize.tooltip")}</p>
              </TooltipContent>
            </Tooltip>
          )}

          {poll.status !== "archived" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleArchive}
                  className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                  data-testid="poll-action-archive"
                >
                  <Archive className="w-4 h-4" />
                  {variant === "full" && (
                    <span className="hidden sm:inline">
                      {t("actions.archive.label")}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("actions.archive.tooltip")}</p>
              </TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleDelete}
                className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                data-testid="poll-action-delete"
              >
                <Trash2 className="w-4 h-4" />
                {variant === "full" && (
                  <span className="hidden sm:inline">
                    {t("actions.delete.label")}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("actions.delete.tooltip")}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        {showQR && poll.status !== "draft" && (
          <div className="mt-3 flex justify-start">
            <QRCodeDisplay
              url={buildPublicLink(poll.slug)}
              size={150}
              label={t("actions.qr.scanLabel")}
            />
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default PollActions;
